import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Minus, X, Search, ShoppingCart, Loader2, AlertTriangle, Check } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { AdminQuantityDialog } from "@/components/admin-quantity-dialog";
import { getAvailableFulfillmentDates, getStaffFulfillmentDates, getFulfillmentDateOption, orgToday, type FulfillmentDateOption, type WindowConfig } from "@/lib/frontend-periods";
import type { Account, Product, Category } from "@shared/schema";
import { aliasMatchesSearch } from "@shared/productAliases";
import { parseTypedPrice } from "@shared/linePrice";
import { accountMenuPrice, customerTypePath, type CustomerTypeDetail } from "@/lib/customerTypes";

interface DuplicateOrderInfo {
  id: number;
  displayId: string;
  status: string;
  totalItems: number;
}

interface AdminOrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface OrderItem {
  productId: number;
  productName: string;
  quantity: number;
  /** A price typed for this order's line, as typed; blank = the account's menu price. */
  unitPrice?: string;
}

export function AdminOrderModal({ open, onOpenChange }: AdminOrderModalProps) {
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [notes, setNotes] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | "all">("all");
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateOrderInfo, setDuplicateOrderInfo] = useState<DuplicateOrderInfo | null>(null);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountHighlightIndex, setAccountHighlightIndex] = useState(0);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [productHighlightIndex, setProductHighlightIndex] = useState(0);
  const [quickProductSearch, setQuickProductSearch] = useState("");
  const [lastAddedProductId, setLastAddedProductId] = useState<number | null>(null);
  const [fulfillmentType, setFulfillmentType] = useState<"delivery" | "pickup" | "">("");
  const [fulfillmentDate, setFulfillmentDate] = useState<string>("");
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const isSubmittingRef = useRef(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pickerProductId, setPickerProductId] = useState<number | null>(null);
  const accountInputRef = useRef<HTMLInputElement>(null);
  const accountDropdownRef = useRef<HTMLDivElement>(null);
  const productInputRef = useRef<HTMLInputElement>(null);
  const productDropdownRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Detect md: breakpoint (768px) — picker is mobile-only
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsMobile(e.matches);
    handler(mq);
    mq.addEventListener('change', handler as (e: MediaQueryListEvent) => void);
    return () => mq.removeEventListener('change', handler as (e: MediaQueryListEvent) => void);
  }, []);

  const { data: accounts = [], isLoading: accountsLoading } = useQuery<Account[]>({
    queryKey: ["/api/accounts"],
    enabled: open,
  });

  const { data: products = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/products"],
    enabled: open,
  });

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories"],
    enabled: open,
  });

  const selectedAccount = accounts.find(a => a.id.toString() === selectedAccountId);

  // The account's customer type: a blank line price is saved at the type's price (the
  // server resolves it), so the greyed price in each line's box is that price too.
  const { data: selectedAccountType } = useQuery<CustomerTypeDetail>({
    queryKey: [customerTypePath(selectedAccount?.customerTypeId ?? 0)],
    enabled: open && selectedAccount?.customerTypeId != null,
  });
  const accountType = selectedAccount?.customerTypeId != null ? selectedAccountType : null;

  const { data: windowConfig } = useQuery<WindowConfig>({
    queryKey: selectedAccount
      ? ["/api/order-window-config/resolved", selectedAccount.id]
      : ["/api/order-window-config"],
    enabled: open,
  });

  const fulfillmentOptions = useMemo(() => {
    if (!windowConfig) return { deliveryEnabled: false, pickupEnabled: false };
    return {
      deliveryEnabled: windowConfig.deliveryEnabled !== false,
      pickupEnabled: windowConfig.pickupEnabled === true,
    };
  }, [windowConfig]);

  const [dateRefreshKey, setDateRefreshKey] = useState(0);
  useEffect(() => {
    if (open) {
      setDateRefreshKey(k => k + 1);
    }
  }, [open]);

  // Staff pick from the staff list (the last scheduled day before today, then every
  // scheduled day ahead, cutoff passed or not) — "Custom date..." takes any other. The
  // default stays the first date a customer could still pick, else the next scheduled
  // day, so after a one-day-a-week account's cutoff the list is never empty.
  const { availableDates, defaultDate } = useMemo((): { availableDates: FulfillmentDateOption[]; defaultDate: string } => {
    if (!windowConfig || !fulfillmentType) return { availableDates: [], defaultDate: "" };
    const type = fulfillmentType as 'delivery' | 'pickup';
    try {
      const staffDates = getStaffFulfillmentDates(type, windowConfig, { maxDates: 10 });
      const [firstOffered] = getAvailableFulfillmentDates(type, windowConfig, { maxDates: 1 });
      const today = orgToday(windowConfig);
      return {
        availableDates: staffDates,
        defaultDate: firstOffered?.value ?? staffDates.find(d => d.value >= today)?.value ?? "",
      };
    } catch {
      return { availableDates: [], defaultDate: "" };
    }
  }, [windowConfig, fulfillmentType, dateRefreshKey]);

  const [showCustomDateInput, setShowCustomDateInput] = useState(false);

  // A custom date the list doesn't hold still shows in the select.
  const dateOptions = useMemo(() => {
    if (!windowConfig || !fulfillmentDate || availableDates.some(d => d.value === fulfillmentDate)) {
      return availableDates;
    }
    return [...availableDates, getFulfillmentDateOption(fulfillmentDate, windowConfig)];
  }, [availableDates, fulfillmentDate, windowConfig]);

  useEffect(() => {
    if (!open) return;
    if (!fulfillmentOptions.deliveryEnabled && !fulfillmentOptions.pickupEnabled) {
      setFulfillmentType("");
      setFulfillmentDate("");
    } else if (fulfillmentOptions.deliveryEnabled && !fulfillmentOptions.pickupEnabled) {
      setFulfillmentType("delivery");
    } else if (!fulfillmentOptions.deliveryEnabled && fulfillmentOptions.pickupEnabled) {
      setFulfillmentType("pickup");
    } else if (fulfillmentOptions.deliveryEnabled && fulfillmentOptions.pickupEnabled) {
      // Both enabled — default to delivery, but don't override user's explicit choice
      setFulfillmentType(prev => prev || "delivery");
    }
  }, [open, fulfillmentOptions.deliveryEnabled, fulfillmentOptions.pickupEnabled]);

  useEffect(() => {
    setFulfillmentDate(fulfillmentType ? defaultDate : "");
    setShowCustomDateInput(false);
  }, [fulfillmentType, defaultDate, availableDates]);

  const categoryMap = useMemo(() => {
    const map: Record<number, string> = {};
    categories.forEach(cat => {
      map[cat.id] = cat.name;
    });
    return map;
  }, [categories]);

  const filteredAccounts = useMemo(() => {
    const orderable = accounts.filter(a => a.isActive && !a.isGroupParent);
    if (!accountSearch) return orderable;
    const search = accountSearch.toLowerCase();
    return orderable.filter(a =>
      a.name.toLowerCase().includes(search) ||
      (a.phone && a.phone.includes(search))
    );
  }, [accounts, accountSearch]);

  const quickFilteredProducts = useMemo(() => {
    if (!quickProductSearch) return [];
    const search = quickProductSearch.toLowerCase();
    return products.filter(p =>
      p.isActive && (
        p.name.toLowerCase().includes(search) ||
        categoryMap[p.categoryId]?.toLowerCase().includes(search) ||
        aliasMatchesSearch(p.aliases, search)
      )
    ).slice(0, 8);
  }, [products, quickProductSearch, categoryMap]);

  const displayProducts = useMemo(() => {
    let prods = products.filter(p => p.isActive);
    if (selectedCategoryId !== "all") {
      prods = prods.filter(p => p.categoryId === selectedCategoryId);
    }
    if (quickProductSearch.trim()) {
      const search = quickProductSearch.toLowerCase().trim();
      prods = prods.filter(p => p.name.toLowerCase().includes(search));
    }
    return prods;
  }, [products, selectedCategoryId, quickProductSearch]);

  useEffect(() => {
    setAccountHighlightIndex(0);
  }, [accountSearch]);

  useEffect(() => {
    setProductHighlightIndex(0);
  }, [quickProductSearch]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(e.target as Node) &&
          accountInputRef.current && !accountInputRef.current.contains(e.target as Node)) {
        setAccountDropdownOpen(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(e.target as Node) &&
          productInputRef.current && !productInputRef.current.contains(e.target as Node)) {
        setProductDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectAccount = useCallback((account: Account) => {
    setSelectedAccountId(account.id.toString());
    setAccountSearch(account.name);
    setAccountDropdownOpen(false);
  }, []);

  const handleAccountKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredAccounts.length > 0) {
        const idx = accountDropdownOpen ? accountHighlightIndex : 0;
        selectAccount(filteredAccounts[Math.min(idx, filteredAccounts.length - 1)]);
      }
      return;
    }

    if (!accountDropdownOpen) {
      if (e.key === 'ArrowDown') {
        setAccountDropdownOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setAccountHighlightIndex(prev => Math.min(prev + 1, filteredAccounts.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setAccountHighlightIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Escape':
        e.preventDefault();
        setAccountDropdownOpen(false);
        break;
      case 'Tab':
        setAccountDropdownOpen(false);
        break;
    }
  };

  const selectProduct = (product: Product) => {
    addItem(product);
    setQuickProductSearch("");
    setProductDropdownOpen(false);
    setTimeout(() => productInputRef.current?.focus(), 0);
  };

  const handleProductKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (quickFilteredProducts.length > 0) {
        const idx = productDropdownOpen ? productHighlightIndex : 0;
        selectProduct(quickFilteredProducts[Math.min(idx, quickFilteredProducts.length - 1)]);
      }
      return;
    }

    if (!productDropdownOpen) {
      if (e.key === 'ArrowDown' && quickProductSearch) {
        setProductDropdownOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setProductHighlightIndex(prev => Math.min(prev + 1, quickFilteredProducts.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setProductHighlightIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Escape':
        e.preventDefault();
        setProductDropdownOpen(false);
        setQuickProductSearch("");
        break;
      case 'Tab':
        setProductDropdownOpen(false);
        break;
    }
  };

  const createOrderMutation = useMutation({
    mutationFn: async (data: { accountId: number; items: OrderItem[]; notes: string; fulfillmentType?: string; fulfillmentDate?: string }) => {
      const body: any = {
        accountId: data.accountId,
        items: data.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          ...(item.unitPrice?.trim() ? { unitPrice: item.unitPrice.trim() } : {}),
        })),
        notes: data.notes
      };
      if (data.fulfillmentType) body.fulfillmentType = data.fulfillmentType;
      if (data.fulfillmentDate) body.fulfillmentDate = data.fulfillmentDate;
      return apiRequest("POST", "/api/orders/admin", body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      toast({
        title: "Order created",
        description: "New order has been created successfully.",
      });
      handleClose();
    },
    onError: (error: any) => {
      // 409 PRODUCTS_UNAVAILABLE: a product in the cart was deactivated (or is not this
      // org's) since the list loaded. Nothing was written; name what to remove.
      let unavailableIds: number[] | null = null;
      try {
        const body = JSON.parse(String(error?.message ?? "").replace(/^\d+:\s*/, ""));
        if (body?.code === "PRODUCTS_UNAVAILABLE") unavailableIds = body.unavailable?.productIds ?? [];
      } catch {}
      if (unavailableIds) {
        const names = unavailableIds.map(id => products.find(p => p.id === id)?.name ?? `Product #${id}`);
        toast({
          title: "Order not created",
          description: `No longer available: ${names.join(", ")}. Remove ${names.length === 1 ? "it" : "them"} and try again.`,
          variant: "destructive",
        });
        return;
      }
      toast({
        title: "Failed to create order",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      isSubmittingRef.current = false;
      setIsCheckingDuplicate(false);
    },
  });

  const mergeOrderMutation = useMutation({
    mutationFn: async (data: { orderId: number; items: OrderItem[]; notes: string }) => {
      return apiRequest("PATCH", `/api/orders/${data.orderId}/merge`, {
        items: data.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          ...(item.unitPrice?.trim() ? { unitPrice: item.unitPrice.trim() } : {}),
        })),
        notes: data.notes
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      toast({
        title: "Items merged",
        description: "Items have been added to the existing order.",
      });
      setShowDuplicateWarning(false);
      handleClose();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to merge order",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      setShowDuplicateWarning(false);
    },
    onSettled: () => {
      isSubmittingRef.current = false;
      setIsCheckingDuplicate(false);
    },
  });

  const handleClose = () => {
    setSelectedAccountId("");
    setOrderItems([]);
    setNotes("");
    setAccountSearch("");
    setSelectedCategoryId("all");
    setShowDuplicateWarning(false);
    setDuplicateOrderInfo(null);
    setAccountDropdownOpen(false);
    setProductDropdownOpen(false);
    setQuickProductSearch("");
    setFulfillmentType("");
    setFulfillmentDate("");
    onOpenChange(false);
  };

  const addItem = (product: Product) => {
    setOrderItems(prev => {
      const existing = prev.find(item => item.productId === product.id);
      if (existing) {
        return prev.map(item =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        setLastAddedProductId(product.id);
        return [...prev, {
          productId: product.id,
          productName: product.name,
          quantity: 1
        }];
      }
    });
  };

  useEffect(() => {
    if (lastAddedProductId !== null && open) {
      const frameId = requestAnimationFrame(() => {
        const input = document.querySelector(`[data-testid="input-quantity-${lastAddedProductId}"]`) as HTMLInputElement;
        if (input) {
          input.focus({ preventScroll: true });
          input.select();
        }
        setLastAddedProductId(null);
      });
      return () => cancelAnimationFrame(frameId);
    }
  }, [lastAddedProductId, open]);

  const updateQuantity = (productId: number, delta: number) => {
    setOrderItems(orderItems.map(item => {
      if (item.productId === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const setItemQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    setOrderItems(orderItems.map(item => {
      if (item.productId === productId) {
        return { ...item, quantity };
      }
      return item;
    }));
  };

  const removeItem = (productId: number) => {
    setOrderItems(orderItems.filter(item => item.productId !== productId));
  };

  // A price typed for one line of this order (kept as typed; parsed on the server too).
  const setItemPrice = (productId: number, unitPrice: string) => {
    setOrderItems(prev => prev.map(item => (item.productId === productId ? { ...item, unitPrice } : item)));
  };
  const priceErrors = orderItems
    .map(item => ({ item, parsed: parseTypedPrice(item.unitPrice?.trim() || undefined) }))
    .filter(({ parsed }) => "error" in parsed);

  const setOrAddItem = (product: Product, quantity: number) => {
    if (quantity <= 0) {
      removeItem(product.id);
      return;
    }
    setOrderItems(prev => {
      const existing = prev.find(item => item.productId === product.id);
      if (existing) {
        return prev.map(item =>
          item.productId === product.id ? { ...item, quantity } : item
        );
      }
      return [...prev, {
        productId: product.id,
        productName: product.name,
        quantity,
      }];
    });
  };

  const checkForDuplicateAndSubmit = async () => {
    if (isSubmittingRef.current) return;

    if (!selectedAccountId) {
      toast({
        title: "Select a customer",
        description: "Please select a customer for this order.",
        variant: "destructive",
      });
      return;
    }
    if (orderItems.length === 0) {
      toast({
        title: "Add items",
        description: "Please add at least one item to the order.",
        variant: "destructive",
      });
      return;
    }

    if ((fulfillmentOptions.deliveryEnabled || fulfillmentOptions.pickupEnabled) && !fulfillmentDate) {
      toast({
        title: "Select fulfillment date",
        description: "Please choose a fulfillment method and date for this order.",
        variant: "destructive",
      });
      return;
    }

    isSubmittingRef.current = true;
    setIsCheckingDuplicate(true);

    try {
      // Date-scope the duplicate check (Phase 2): only warn about / merge into a same-date order,
      // so an admin composing a second dated order doesn't merge into the wrong date's order.
      const dupUrl = fulfillmentDate
        ? `/api/orders/check-duplicate/${selectedAccountId}?fulfillmentDate=${encodeURIComponent(fulfillmentDate)}`
        : `/api/orders/check-duplicate/${selectedAccountId}`;
      const response = await apiRequest("GET", dupUrl);
      const data = await response.json();

      if (data.hasDuplicate && data.existingOrder) {
        setDuplicateOrderInfo(data.existingOrder);
        setShowDuplicateWarning(true);
        // Release the guard — user will make an explicit choice in the AlertDialog
        isSubmittingRef.current = false;
        setIsCheckingDuplicate(false);
      } else {
        createNewOrder();
        // Guard released by createOrderMutation.onSettled
      }
    } catch (error) {
      console.error("Failed to check for duplicates:", error);
      isSubmittingRef.current = false;
      setIsCheckingDuplicate(false);
      createNewOrder();
    }
  };

  const createNewOrder = () => {
    createOrderMutation.mutate({
      accountId: parseInt(selectedAccountId),
      items: orderItems,
      notes,
      fulfillmentType: fulfillmentType || undefined,
      fulfillmentDate: fulfillmentDate || undefined,
    });
  };

  const mergeWithExistingOrder = () => {
    if (!duplicateOrderInfo) return;
    mergeOrderMutation.mutate({
      orderId: duplicateOrderInfo.id,
      items: orderItems,
      notes
    });
  };

  const totalItems = orderItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="dialog-admin-order">
        <DialogTitle className="sr-only">Create New Order</DialogTitle>

        {/* Header bar */}
        <div className="flex items-center px-5 py-3 border-b">
          <div className="flex items-center gap-2.5">
            <ShoppingCart className="h-4 w-4" />
            <span className="text-base font-medium">Create order</span>
          </div>
        </div>

        {/* Config strip */}
        <div className="px-4 py-3 bg-gray-50 border-b space-y-2">
          {/* Row 1: Customer search — full width */}
          <div className="relative">
            <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5 w-full">
              <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              {selectedAccountId && selectedAccount ? (
                <>
                  <span className="text-sm font-medium truncate">{selectedAccount.name}</span>
                  <Check className="h-3.5 w-3.5 text-green-600 ml-auto flex-shrink-0" />
                  <button
                    onClick={() => {
                      setSelectedAccountId("");
                      setAccountSearch("");
                      accountInputRef.current?.focus();
                    }}
                  >
                    <X className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                  </button>
                </>
              ) : (
                <Input
                  ref={accountInputRef}
                  placeholder="Search customers..."
                  value={accountSearch}
                  onChange={(e) => {
                    setAccountSearch(e.target.value);
                    setSelectedAccountId("");
                    setAccountDropdownOpen(true);
                  }}
                  onFocus={() => {
                    if (!selectedAccountId) setAccountDropdownOpen(true);
                  }}
                  onKeyDown={handleAccountKeyDown}
                  className="border-0 shadow-none h-auto p-0 text-sm focus-visible:ring-0"
                  data-testid="input-customer-search"
                />
              )}
            </div>
            {accountDropdownOpen && (
              <div
                ref={accountDropdownRef}
                className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border rounded-md shadow-lg max-h-[38vh]8 overflow-y-auto"
              >
                {accountsLoading ? (
                  <div className="p-3 text-center text-gray-500 text-sm">Loading...</div>
                ) : filteredAccounts.length === 0 ? (
                  <div className="p-3 text-center text-gray-500 text-sm">No customers found</div>
                ) : (
                  filteredAccounts.map((account, index) => (
                    <div
                      key={account.id}
                      className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm ${
                        index === accountHighlightIndex ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => selectAccount(account)}
                      onMouseEnter={() => setAccountHighlightIndex(index)}
                      data-testid={`account-option-${account.id}`}
                    >
                      <span className="font-medium">{account.name}</span>
                      {account.phone && (
                        <span className="text-gray-400 text-xs">{account.phone}</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Row 2: Fulfillment toggle + Date — side by side */}
          {(fulfillmentOptions.deliveryEnabled || fulfillmentOptions.pickupEnabled) && (
            <div className="flex items-center gap-2 flex-wrap">
              {fulfillmentOptions.deliveryEnabled && fulfillmentOptions.pickupEnabled ? (
                <div className="flex rounded-lg border overflow-hidden bg-white flex-shrink-0 w-fit">
                  <button
                    className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                      fulfillmentType === "delivery"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                    onClick={() => setFulfillmentType("delivery")}
                  >
                    Delivery
                  </button>
                  <button
                    className={`px-3 py-1.5 text-xs font-medium border-l transition-colors ${
                      fulfillmentType === "pickup"
                        ? "bg-green-50 text-green-700"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                    onClick={() => setFulfillmentType("pickup")}
                  >
                    Pickup
                  </button>
                </div>
              ) : (
                <Badge variant="secondary" className="text-xs flex-shrink-0">
                  {fulfillmentOptions.deliveryEnabled ? "Delivery" : "Pickup"}
                </Badge>
              )}


              {fulfillmentType ? (
                showCustomDateInput ? (
                  <input
                    type="date"
                    className="w-[160px] h-8 text-xs border border-gray-200 rounded-md px-2 flex-shrink-0"
                    autoFocus
                    defaultValue={fulfillmentDate}
                    onChange={(e) => {
                      if (e.target.value) {
                        setFulfillmentDate(e.target.value);
                        setShowCustomDateInput(false);
                      }
                    }}
                    onBlur={() => setShowCustomDateInput(false)}
                  />
                ) : (
                  <Select
                    value={fulfillmentDate}
                    onValueChange={(value) => {
                      if (value === "__custom") setShowCustomDateInput(true);
                      else setFulfillmentDate(value);
                    }}
                  >
                    <SelectTrigger className="w-[160px] h-8 text-xs flex-shrink-0">
                      <SelectValue placeholder="Select date..." />
                    </SelectTrigger>
                    <SelectContent>
                      {dateOptions.map(d => (
                        <SelectItem key={d.value} value={d.value}>
                          {d.label}{d.sublabel ? ` (${d.sublabel})` : ''}
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom" className="text-gray-500">Custom date...</SelectItem>
                    </SelectContent>
                  </Select>
                )
              ) : null}
            </div>
          )}
        </div>

        {/* Main body: horizontal split */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* LEFT: Product browser */}
          <div className="flex-[3] flex flex-col border-r min-h-[300px] min-w-0 max-h-[45vh] md:max-h-none">
            {/* Category pill tabs */}
            <div className="relative">
              <div className="flex gap-1.5 px-4 py-2.5 border-b overflow-x-auto scrollbar-hide">
                <button
                  className={`px-3.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                    selectedCategoryId === "all"
                      ? "bg-gray-900 text-white"
                      : "text-gray-500 border border-gray-200 hover:bg-gray-50"
                  }`}
                  onClick={() => setSelectedCategoryId("all")}
                >
                  All
                </button>
                {categories.filter(c => c.isActive !== false).map(category => {
                  const itemsInCategory = orderItems.filter(item =>
                    products.some(p => p.id === item.productId && p.categoryId === category.id)
                  );
                  const totalQty = itemsInCategory.reduce((sum, item) => sum + item.quantity, 0);
                  return (
                    <button
                      key={category.id}
                      className={`px-3.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                        selectedCategoryId === category.id
                          ? "bg-gray-900 text-white"
                          : "text-gray-500 border border-gray-200 hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedCategoryId(category.id)}
                    >
                      {category.name}
                      {totalQty > 0 && (
                        <span className="ml-1.5 bg-white/20 px-1.5 rounded-full text-[10px]">
                          {totalQty}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
            </div>

            {/* Unified product search (Quick Add) */}
            <div className="px-4 py-2 border-b relative">
              <Search className="absolute left-7 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                ref={productInputRef}
                placeholder="Search products..."
                value={quickProductSearch}
                onChange={(e) => {
                  setQuickProductSearch(e.target.value);
                  setProductDropdownOpen(!!e.target.value);
                }}
                onFocus={() => {
                  if (quickProductSearch) setProductDropdownOpen(true);
                }}
                onKeyDown={handleProductKeyDown}
                className="pl-9 h-9 text-sm"
                data-testid="input-quick-product-search"
              />
              {quickProductSearch && (
                <button
                  className="absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    setQuickProductSearch("");
                    setProductDropdownOpen(false);
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              {productDropdownOpen && quickProductSearch && (
                <div
                  ref={productDropdownRef}
                  className="absolute z-50 left-4 right-4 top-full mt-1 bg-white border rounded-md shadow-lg max-h-[38vh]8 overflow-y-auto"
                >
                  {quickFilteredProducts.length === 0 ? (
                    <div className="p-3 text-center text-gray-500 text-sm">No products found</div>
                  ) : (
                    quickFilteredProducts.map((product, index) => {
                      const inCart = orderItems.find(item => item.productId === product.id);
                      return (
                        <div
                          key={product.id}
                          className={`flex items-center justify-between px-3 py-2 cursor-pointer text-sm ${
                            index === productHighlightIndex ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50'
                          }`}
                          onClick={() => selectProduct(product)}
                          onMouseEnter={() => setProductHighlightIndex(index)}
                          data-testid={`quick-product-option-${product.id}`}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{product.name}</span>
                            <span className="text-xs text-gray-400">{categoryMap[product.categoryId] || ''}</span>
                          </div>
                          {inCart && (
                            <Badge variant="secondary" className="text-xs">{inCart.quantity} in order</Badge>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Product list */}
            <div className="flex-1 overflow-y-auto px-4 pb-3 pt-1">
              {productsLoading ? (
                <div className="p-4 text-center text-gray-500 text-sm">Loading products...</div>
              ) : displayProducts.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">No products found</div>
              ) : selectedCategoryId === "all" ? (
                categories.filter(c => c.isActive !== false).map(category => {
                  const categoryProducts = displayProducts
                    .filter(p => p.categoryId === category.id)
                    .sort((a, b) => a.name.localeCompare(b.name));
                  if (categoryProducts.length === 0) return null;
                  return (
                    <div key={category.id}>
                      <div className="text-[11px] font-medium text-gray-400 uppercase tracking-wider px-1 pt-3 pb-1 sticky top-0 bg-white z-10">
                        {category.name}
                      </div>
                      {categoryProducts.map(product => {
                        const inCart = orderItems.find(item => item.productId === product.id);
                        return (
                          <div
                            key={product.id}
                            className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer mb-0.5 transition-colors ${
                              inCart ? "bg-blue-50" : "hover:bg-gray-50"
                            }`}
                            onClick={() => {
                              if (isMobile) {
                                setPickerProductId(product.id);
                              } else {
                                addItem(product);
                              }
                            }}
                            data-testid={`product-item-${product.id}`}
                          >
                            <span className={`text-sm ${inCart ? "font-medium" : ""}`}>{product.name}</span>
                            <div className="flex items-center gap-1.5">
                              {inCart && (
                                <span className="text-xs font-medium text-blue-600">{inCart.quantity}</span>
                              )}
                              <Plus className={`h-3.5 w-3.5 ${inCart ? "text-blue-500" : "text-gray-300"}`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })
              ) : (
                displayProducts
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(product => {
                    const inCart = orderItems.find(item => item.productId === product.id);
                    return (
                      <div
                        key={product.id}
                        className={`flex items-center justify-between px-3 py-2.5 rounded-lg cursor-pointer mb-0.5 transition-colors ${
                          inCart ? "bg-blue-50" : "hover:bg-gray-50"
                        }`}
                        onClick={() => {
                          if (isMobile) {
                            setPickerProductId(product.id);
                          } else {
                            addItem(product);
                          }
                        }}
                        data-testid={`product-item-${product.id}`}
                      >
                        <span className={`text-sm ${inCart ? "font-medium" : ""}`}>{product.name}</span>
                        <div className="flex items-center gap-1.5">
                          {inCart && (
                            <span className="text-xs font-medium text-blue-600">{inCart.quantity}</span>
                          )}
                          <Plus className={`h-3.5 w-3.5 ${inCart ? "text-blue-500" : "text-gray-300"}`} />
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>

          {/* RIGHT: Cart panel */}
          <div className="flex-[2] flex flex-col bg-gray-50 min-h-[250px]">
            {/* Cart header */}
            <div className="px-4 py-2.5 border-b flex items-center justify-between">
              <span className="text-sm font-medium">Order items</span>
              <span className="text-xs text-gray-500">{orderItems.length} item{orderItems.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Cart items — scrollable */}
            <div className="flex-1 overflow-y-auto px-3 py-2">
              {orderItems.length === 0 ? (
                <div className="flex items-center justify-center h-full text-sm text-gray-400">
                  Click products to add them
                </div>
              ) : (
                <div className="space-y-1.5">
                  {orderItems.map(item => (
                    <div
                      key={item.productId}
                      className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border"
                      data-testid={`order-item-${item.productId}`}
                    >
                      <span className="text-sm font-medium flex-1 truncate">{item.productName}</span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          className="w-6 h-6 flex items-center justify-center rounded border hover:bg-gray-50"
                          onClick={() => updateQuantity(item.productId, -1)}
                          data-testid={`btn-decrease-${item.productId}`}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          key={`qty-${item.productId}-${item.quantity}`}
                          defaultValue={item.quantity}
                          onFocus={(e) => e.target.select()}
                          onBlur={(e) => {
                            const num = parseInt(e.target.value) || 0;
                            if (num > 0) {
                              setItemQuantity(item.productId, num);
                            } else {
                              e.target.value = String(item.quantity);
                            }
                          }}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                          className="w-10 h-6 text-center text-sm font-medium border rounded bg-white"
                          data-testid={`input-quantity-${item.productId}`}
                        />
                        <button
                          className="w-6 h-6 flex items-center justify-center rounded border hover:bg-gray-50"
                          onClick={() => updateQuantity(item.productId, 1)}
                          data-testid={`btn-increase-${item.productId}`}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                      {/* A price for this order's line; blank = the account's menu price. */}
                      <label className="flex items-center gap-0.5 flex-shrink-0 text-xs text-gray-400" title="Price for this order. Blank: the account's menu price.">
                        $
                        <input
                          type="text"
                          inputMode="decimal"
                          value={item.unitPrice ?? ''}
                          placeholder={(() => { const product = products.find(p => p.id === item.productId); return product ? accountMenuPrice(product, accountType) ?? '' : ''; })()}
                          onChange={(e) => setItemPrice(item.productId, e.target.value)}
                          className={`w-16 h-6 px-1 text-right text-sm border rounded bg-white ${"error" in parseTypedPrice(item.unitPrice?.trim() || undefined) ? 'border-red-400' : ''}`}
                          data-testid={`input-price-${item.productId}`}
                        />
                      </label>
                      <button
                        className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
                        onClick={() => removeItem(item.productId)}
                        data-testid={`btn-remove-${item.productId}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="px-3 py-2">
              <Textarea
                placeholder="Add notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={1}
                className="text-xs min-h-[36px] resize-none bg-white"
                data-testid="input-order-notes"
              />
            </div>

            {/* Create button — pinned at bottom */}
            <div className="px-4 py-3 border-t">
              {priceErrors.length > 0 && (
                <p className="mb-2 text-xs text-red-600" data-testid="text-price-error">
                  {priceErrors[0].item.productName}: {("error" in priceErrors[0].parsed && priceErrors[0].parsed.error) || "Check the price."}
                </p>
              )}
              <Button
                className="w-full bg-[#3a6b35] hover:bg-[#2d5429] text-white"
                onClick={checkForDuplicateAndSubmit}
                disabled={isCheckingDuplicate || createOrderMutation.isPending || mergeOrderMutation.isPending || !selectedAccountId || orderItems.length === 0 || priceErrors.length > 0}
                data-testid="btn-create-order"
              >
                {isCheckingDuplicate || createOrderMutation.isPending || mergeOrderMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>Create order ({totalItems} items)</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>

      {pickerProductId !== null && (() => {
        const pickerProduct = products.find(p => p.id === pickerProductId);
        const existingItem = orderItems.find(item => item.productId === pickerProductId);
        const currentQuantity = existingItem?.quantity ?? 0;
        return (
          <AdminQuantityDialog
            isOpen={true}
            onClose={() => setPickerProductId(null)}
            productName={pickerProduct?.name ?? ''}
            initialQuantity={currentQuantity}
            onConfirm={(qty) => {
              if (pickerProduct) {
                setOrAddItem(pickerProduct, qty);
              }
            }}
          />
        );
      })()}

      <AlertDialog open={showDuplicateWarning} onOpenChange={setShowDuplicateWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Existing Order Found
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left break-words whitespace-normal overflow-hidden">
              {duplicateOrderInfo && selectedAccount && (
                <div className="space-y-3">
                  <p>
                    <strong>{selectedAccount.name}</strong> already has a <strong>{duplicateOrderInfo.status}</strong> order (<strong>{duplicateOrderInfo.displayId}</strong>) in this order period with {duplicateOrderInfo.totalItems} item(s).
                  </p>
                  <p>
                    Would you like to add these items to the existing order, or create a separate order?
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowDuplicateWarning(false)}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              onClick={createNewOrder}
              disabled={createOrderMutation.isPending}
            >
              Create Separate Order
            </Button>
            <AlertDialogAction
              onClick={mergeWithExistingOrder}
              disabled={mergeOrderMutation.isPending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {mergeOrderMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Merging...
                </>
              ) : (
                `Add to ${duplicateOrderInfo?.displayId || 'Existing Order'}`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
